/* eslint-disable react/react-in-jsx-scope */
import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';

export const baseURI = 'recaudacion/zona-de-diligencias-pae/';
/*
  ZONAS DE DILIGENCIA OBJECT
  {
    "id": 4,
    "clave": "4",
    "descripcion": "MANZANA 3",
    "observaciones": null,
    "zona_superior_dependiente": 3,
    "visible_en_presentacion": true,
    "seleccionable_en_presentacion": true,
    "orden_de_presentacion": null,
    "estados_globales": 1
  }
*/

/**
 * GET Zonas de Diligencia
 * param {object} params
 * @returns {Promise<object>}
 */
export const getZonasDeDiligencia = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data.sort((a, b) => a.orden_de_presentacion - b.orden_de_presentacion);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Zona de Diligencia
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getZonaDeDiligencia = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Zona de Diligencia
  * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postZonaDeDiligencia = async (values) => {
  try {
    const response = await API.post(baseURI, values);
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Zona de Diligencia
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchZonaDeDiligencia = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, values);
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Zona de Diligencia
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const deleteZonaDeDiligencia = async (id) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, {
      estados_globales: ESTADOS_GLOBALES_MAP.CANCELADO,
    });
    onSuccess(response, 'Cancelado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * Arbola las zonas de diligencia en una estructura jerárquica
 * @param {Array} zonas - Array de zonas de diligencia
 * @returns {Array} Array de zonas organizadas en estructura de árbol
 */
export const arbolarZonasDeDiligencia = (zonas) => {
  if (!Array.isArray(zonas) || zonas.length === 0) {
    return [];
  }

  // Crear un mapa para acceso rápido por ID
  const zonasMap = new Map();
  const zonasRaiz = [];

  // Inicializar el mapa y marcar todas las zonas
  zonas.forEach((zona) => {
    // Crear descripción con JSX si es seleccionable
    let descripcionJSX = zona.descripcion;
    if (zona.seleccionable_en_presentacion) {
      // eslint-disable-next-line react/jsx-filename-extension
      descripcionJSX = <strong>{zona.descripcion}</strong>;
    }

    zonasMap.set(zona.id, {
      ...zona,
      children: [],
      label: descripcionJSX,
      value: zona.id,
      selectable: zona.seleccionable_en_presentacion,
    });
  });

  // Construir la estructura jerárquica
  zonas.forEach((zona) => {
    const zonaActual = zonasMap.get(zona.id);

    if (zona.zona_superior_dependiente && zonasMap.has(zona.zona_superior_dependiente)) {
      // Si tiene zona superior, agregar como dependiente
      const zonaSuperior = zonasMap.get(zona.zona_superior_dependiente);
      zonaSuperior.children.push(zonaActual);
    } else {
      // Si no tiene zona superior o la zona superior no existe, es una zona raíz
      zonasRaiz.push(zonaActual);
    }
  });

  // Ordenar las zonas raíz por orden_de_presentacion
  zonasRaiz.sort((a, b) => {
    const ordenA = a.orden_de_presentacion || 0;
    const ordenB = b.orden_de_presentacion || 0;
    return ordenA - ordenB;
  });

  // Función recursiva para ordenar zonas dependientes
  const ordenarZonasDependientes = (zonasArray) => {
    zonasArray.forEach((zona) => {
      zona.children.sort((a, b) => {
        const ordenA = a.orden_de_presentacion || 0;
        const ordenB = b.orden_de_presentacion || 0;
        return ordenA - ordenB;
      });

      // Ordenar recursivamente las zonas dependientes
      if (zona.children.length > 0) {
        ordenarZonasDependientes(zona.children);
      }
    });
  };

  ordenarZonasDependientes(zonasRaiz);

  return zonasRaiz;
};

/**
 * Obtiene todas las zonas de diligencia y las arbola
 * @param {object} params - Parámetros para la consulta
 * @returns {Promise<Array>} Array de zonas organizadas en estructura de árbol
 */
export const getZonasDeDiligenciaArboladas = async (params) => {
  try {
    const zonas = await getZonasDeDiligencia(params);
    return arbolarZonasDeDiligencia(zonas);
  } catch (err) {
    onError(err);
    return [];
  }
};
